import { Component, HostListener, inject, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AuthenticationContants } from 'src/app/constants/authentication-constants';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { MaintenanceSchedulingModel } from 'src/app/models/maintenance/maintenance-scheduling.model';
import { WindowService } from 'src/app/services/authentication/window.service';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { DateConverterService } from 'src/app/services/global-services/date-converter.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { MaintenanceSchedulingService } from 'src/app/services/maintenance/maintenance-scheduling.service';
import { BranchService } from 'src/app/services/management/branch.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { TypeMaintenanceSchedulingEnums } from 'src/app/enum/maintenance-scheduling.enums';

@Component({
  selector: 'app-register-maintenance-scheduling',
  templateUrl: './register-maintenance-scheduling.component.html',
  styleUrls: ['./register-maintenance-scheduling.component.scss'],
  standalone: false
})
export class RegisterMaintenanceSchedulingComponent implements OnInit {

  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly branchService = inject(BranchService);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly dateConverterService = inject(DateConverterService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly headerService = inject(HeaderService);
  private readonly location = inject(Location);
  private readonly maintenanceSchedulingService = inject(MaintenanceSchedulingService);
  private readonly title = inject(Title);
  private readonly toastHandlerService = inject(ToastHandlerService);
  private readonly windowService = inject(WindowService);

  branchId: number = 0;
  companyId: number = 0;
  disabledForm: boolean = true;
  formMaintenanceScheduling: FormGroup = new FormGroup({});
  hasQuantityInZero: boolean = false;
  iconState: string = '';
  isOpened: string | undefined = undefined;
  lastDateMaintenanceScheduling: Date | null = null;
  listBranches: any[] = [];
  listTypeMaintenanceScheduling: { label: string; value: number }[] = [
    { label: 'Horómetro', value: TypeMaintenanceSchedulingEnums.horometro },
    { label: 'Día', value: TypeMaintenanceSchedulingEnums.dia }
  ];
  maintenanceSchedulingId: number = 0;
  maintenanceSchedulingModel: MaintenanceSchedulingModel = new MaintenanceSchedulingModel();
  nameWindow: string = 'Captura Programación de Mantenimiento';
  permissionPrint: boolean = false;
  permissionUpdatable: boolean = false;
  permissionsUser: any[] = [];
  projectName = ProjectName.projectName;
  quantityDetails: number = 0;
  severityState: string = '';
  stateMaintenanceScheduling: string = '';
  lastStateMaintenanceScheduling: boolean = true;
  titleState: string = '';
  typeMaintenanceScheduling: number = TypeMaintenanceSchedulingEnums.horometro;
  url: string = 'maintenance-scheduling';

  async ngOnInit() {
    this.validateForm(new MaintenanceSchedulingModel());
    this.headerService.setTitle(this.nameWindow);
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    this.activatedRoute.params.subscribe(params => {
      this.maintenanceSchedulingId = Object.keys(params).length > 0 ? params?.id : null;
    });

    await this.getPermission();
    await this.handleInitialState();
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.ngOnDestroy();
  }

  ngOnDestroy() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      const window = windowsOpened.findIndex(x => x === this.url);
      if (window != -1) {
        windowsOpened.splice(window, 1);
        localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      }
    }
  }

  async changeBranch(branchId: number) {
    this.branchId = branchId;
    this.resetContract();
  }

  async changeConsecutive(consecutive: number) {
    this.branchId = this.formMaintenanceScheduling.get("branchId")?.value;
    if (!this.branchId) {
      return this.toastHandlerService.showToast(
        'warning',
        'Seleccione una sucursal.'
      );
    }
    if (!consecutive) {
      this.resetContract();
      return;
    }
    try {
      const response = await firstValueFrom(
        this.maintenanceSchedulingService.getMaintenanceSchedulingByKeys(this.branchId, consecutive)
      );
      this.maintenanceSchedulingId = response.maintenanceSchedulingId;
      this.maintenanceSchedulingModel = response;
      this.validateForm(this.maintenanceSchedulingModel);
      this.location.replaceState(`/${this.url}/register/${this.maintenanceSchedulingId}`);
    } catch (error) {
      console.error('Error al buscar el medidor.', error);
    }
  }

  async changeDateMaintenanceScheduling(dateMaintenanceScheduling: Date | null) {
    if (dateMaintenanceScheduling) {
      if (this.lastDateMaintenanceScheduling != null && dateMaintenanceScheduling < this.lastDateMaintenanceScheduling) {
        this.toastHandlerService.showToast(
          'warning',
          'La fecha debe ser mayor a la última programación de mantenimiento.'
        );
      }
    }
  }

  async changeTypeMaintenanceScheduling(typeMaintenanceScheduling: number) {
    this.typeMaintenanceScheduling = typeMaintenanceScheduling;
    await this.validateTypeMaintenanceScheduling(typeMaintenanceScheduling);
    if (!this.lastStateMaintenanceScheduling) {
      const type = typeMaintenanceScheduling == TypeMaintenanceSchedulingEnums.horometro ? 'Horómetro' : 'Día';
      this.toastHandlerService.showToast(
        'warning',
        `Debe cerrar la ultima programación de mantenimiento tipo "${type}" para crear una nueva.`
      );
    }
  }

  async createNewRegister() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast(
        'warning',
        `El usuario no tiene permisos para crear una programación de mantenimiento. Comuníquese con el administrador del sistema.`
      );
      return;
    }

    if (!this.branchId) {
      return this.toastHandlerService.showToast(
        'warning',
        'Seleccione una sucursal.'
      );
    }

    const typeMaintenanceScheduling = this.formMaintenanceScheduling.get("typeMaintenanceScheduling")?.value;
    await this.validateTypeMaintenanceScheduling(typeMaintenanceScheduling);
    if (!this.lastStateMaintenanceScheduling) {
      const type = typeMaintenanceScheduling == TypeMaintenanceSchedulingEnums.horometro ? 'Horómetro' : 'Día';
      this.toastHandlerService.showToast(
        'warning',
        `Debe cerrar la ultima programación de mantenimiento tipo "${type}" para crear una nueva.`
      );
    }
    this.resetContract();
  }


  async changeState() {
    const { active, maintenanceSchedulingId } = this.maintenanceSchedulingModel;
    if (!maintenanceSchedulingId || maintenanceSchedulingId === 0) {
      this.toastHandlerService.showToast('warning', 'Debe crear una programación de mantenimiento.');
      return;
    }
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast('error', `No tiene permisos para cambiar el estado de la rutina de mantenimiento.
          Comuníquese con el administrador del sistema.`);
      return;
    }

    if (!active) {
      const quantity = await this.getQuantityWithoutMaintenanceOrder()
      if (quantity > 0) {
        const accepted = await this.confirmDialogService.confirm(`Hay ${quantity} equipos sin ordenes de mtto, ¿Realmente desea cerrar la programación de mantenimiento?`);
        if (!accepted) {
          return;
        }
      }
    }

    const response = await firstValueFrom(this.maintenanceSchedulingService.setActiveMaintenanceScheduling(maintenanceSchedulingId));
    this.maintenanceSchedulingModel = response;
    this.validateForm(this.maintenanceSchedulingModel);
    this.toastHandlerService.showToast('success', 'La programación de mantenimiento fue modificada exitosamente.');
  }

  async createMaintenanceScheduling(modelMaintenanceScheduling: MaintenanceSchedulingModel) {
    try {
      const response = await firstValueFrom(
        this.maintenanceSchedulingService.createMaintenanceScheduling(modelMaintenanceScheduling)
      );
      this.toastHandlerService.showToast(
        'success',
        `La programación de mantenimiento fue creada exitosamente.`
      );
      this.maintenanceSchedulingId = response.maintenanceSchedulingId;
      this.maintenanceSchedulingModel = response;
      this.validateForm(this.maintenanceSchedulingModel);
      this.location.replaceState(`/${this.url}/register/${this.maintenanceSchedulingId}`);
    } catch (error) {
      console.error('Error al crear la programación de mantenimiento.', error);
    }
  }

  async deleteMaintenanceScheduling() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast(
        'error',
        `El usuario no tiene permisos para eliminar la programación de mantenimiento. Comuníquese con el administrador del sistema.`
      );
      return;
    }
    if (!this.maintenanceSchedulingId) {
      this.toastHandlerService.showToast(
        'warning',
        'Debe crear una programación de mantenimiento.'
      );
      return;
    }
    if (this.maintenanceSchedulingModel.active) {
      this.toastHandlerService.showToast(
        'warning',
        'No se puede eliminar la programación de mantenimiento mientras está activa.'
      );
      return;
    }
    if (this.quantityDetails > 0) {
      this.toastHandlerService.showToast(
        'warning',
        'No se puede eliminar la programación de mantenimiento mientras tenga detalle.'
      );
      return;
    }
    const accepted = await this.confirmDialogService.confirm('¿Realmente desea eliminar la programación de mantenimiento?');
    if (accepted) {
      try {
        await firstValueFrom(this.maintenanceSchedulingService.deleteMaintenanceScheduling(this.maintenanceSchedulingId));
        this.toastHandlerService.showToast(
          'success',
          'La programación de mantenimiento fue eliminada exitosamente.'
        );
        this.resetContract();
      } catch (error) {
        console.error('Error al eliminar la programación de mantenimiento.', error);
      }
    }
  }

  async getAllLists() {
    try {
      const response = await firstValueFrom(
        this.branchService.getAllBranchesForSelectAsync()
      );
      this.listBranches = response;
    } catch (error) {
      console.error('Error obtener las listas.', error);
    }
  }

  async getButtonClick(buttonClick: any) {
    this.branchId = this.formMaintenanceScheduling.get("branchId")?.value;
    if (!this.branchId) {
      return this.toastHandlerService.showToast(
        'warning',
        'Seleccione una sucursal.'
      );
    }
    await this.handleMaintenanceSchedulingNavigation(buttonClick, this.branchId)
  }


  async getFirstBranchByUserAuthenticated() {
    try {
      const response = await firstValueFrom(
        this.branchService.getFirstBranchByUserAuthenticatedAsync()
      );
      this.branchId = response.branchId;
    } catch (error) {
      console.error('Error al obtener la sucursal principal del usuario.', error);
    }
  }

  async getMaintenanceSchedulingById() {
    try {
      const response = await firstValueFrom(this.maintenanceSchedulingService.getMaintenanceSchedulingById(this.maintenanceSchedulingId));
      this.branchId = response.branchId;
      this.maintenanceSchedulingModel = response;
      this.validateForm(this.maintenanceSchedulingModel);
    } catch (error) {
      console.error('Error al obtener el medidor.', error);
    }
  }

  async getPermission() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      this.isOpened = windowsOpened.find(x => x === this.url);
      windowsOpened.push(this.url);
      localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      if (this.isOpened) {
        this.toastHandlerService.showToast(
          'error',
          `No se permite tener dos ventanas/pestañas de ${this.nameWindow} abiertas.`
        );
      }
    } else {
      localStorage.clear();
    }
    await Promise.all([
      this.getAllLists(),
      this.getPermissionProfileByUrl(),
      this.getFirstBranchByUserAuthenticated()
    ]);
  }

  async getPermissionProfileByUrl() {
    try {
      const response = await firstValueFrom(
        this.windowService.getPermissionProfileByUrl(this.url)
      );
      this.permissionUpdatable = response;
    } catch (error) {
      console.error('Error al buscar los permisos del perfil.', error);
    }
  }

  async getQuantityWithoutMaintenanceOrder() {
    return await firstValueFrom(this.maintenanceSchedulingService.getTotalWithoutMaintenanceOrder(this.maintenanceSchedulingId));
  }


  async handleInitialState() {
    if (this.maintenanceSchedulingId) {
      await this.getMaintenanceSchedulingById();
    } else {

      this.createNewRegister();
    }
  }

  async handleMaintenanceSchedulingNavigation(buttonClick: any, branchId: number) {
    const consecutive = this.formMaintenanceScheduling.get("consecutive")?.value;
    const actionMap: Record<string, () => Promise<MaintenanceSchedulingModel>> = {
      first: () =>
        firstValueFrom(
          this.maintenanceSchedulingService.getFirstMaintenanceSchedulingByKeys(branchId)
        ),
      previus: () =>
        firstValueFrom(
          this.maintenanceSchedulingService.getPreviousMaintenanceSchedulingByKeys(branchId, consecutive)
        ),
      next: () =>
        firstValueFrom(
          this.maintenanceSchedulingService.getNextMaintenanceSchedulingByKeys(branchId, consecutive)
        ),
      last: () =>
        firstValueFrom(
          this.maintenanceSchedulingService.getLastMaintenanceSchedulingByKeys(branchId)
        ),
    };
    const action = actionMap[buttonClick.target.id];
    if (!action) {
      this.resetContract();
      return;
    }
    try {
      const maintenanceScheduling = await action();
      this.maintenanceSchedulingId = maintenanceScheduling.maintenanceSchedulingId;
      this.maintenanceSchedulingModel = maintenanceScheduling;
      this.validateForm(this.maintenanceSchedulingModel);
      this.location.replaceState(`/${this.url}/register/${this.maintenanceSchedulingId}`);
    } catch (error) {
      console.error(`Error al buscar la programación de mantenimiento (${buttonClick.target.id}).`, error);
    }
  }


  async resetContract() {
    this.maintenanceSchedulingId = 0;
    this.maintenanceSchedulingModel = new MaintenanceSchedulingModel();
    this.maintenanceSchedulingModel.branchId = this.branchId;
    this.validateForm(this.maintenanceSchedulingModel);
    this.location.replaceState(`/${this.url}/register`);
  }

  async send() {
    this.validateFormControls();
    if (this.formMaintenanceScheduling.invalid) {
      this.showValidationMessages();
      return;
    }
    const model = this.formMaintenanceScheduling.getRawValue() as MaintenanceSchedulingModel;
    const lastDate = new Date(this.lastDateMaintenanceScheduling!);
    const currentDate = new Date(model.dateMaintenanceScheduling);

    if (this.lastDateMaintenanceScheduling != null && currentDate < lastDate) {
      this.toastHandlerService.showToast(
        'warning',
        'La fecha debe ser mayor a la última programación de mantenimiento.'
      );
      return;
    }

    model.observation = model.observation?.trim() || null;

    if (this.maintenanceSchedulingId == 0) {
      await this.createMaintenanceScheduling(model);
    } else {
      await this.updateMaintenanceScheduling(model);
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      branchId: {
        required: 'Debe seleccionar una sucursal.',
        min: 'Debe seleccionar una sucursal válida.'
      },
      typeMaintenanceScheduling: {
        required: 'Debe seleccionar un tipo de mantenimiento.',
        min: 'Debe seleccionar un tipo de mantenimiento válido.'
      },
      dateMaintenanceScheduling: {
        required: 'Debe ingresar la fecha de la programación de mantenimiento.'
      },
      observation: {
        maxlength: 'La observación no puede tener más de 250 caracteres.'
      }
    };

    Object.keys(controlMessages).forEach(controlName => {
      const control = this.formMaintenanceScheduling.get(controlName);
      if (control && control.invalid && control.errors) {
        const messages = controlMessages[controlName];
        Object.keys(control.errors).forEach(errorKey => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  async updateMaintenanceScheduling(maintenanceScheduling: MaintenanceSchedulingModel) {
    this.maintenanceSchedulingModel.observation = maintenanceScheduling.observation;
    try {
      const response = await firstValueFrom(
        this.maintenanceSchedulingService.updateMaintenanceScheduling(this.maintenanceSchedulingModel)
      );
      this.toastHandlerService.showToast(
        'success',
        'La programación de mantenimiento fue modificada exitosamente.'
      );
      this.maintenanceSchedulingModel = response;
      this.validateForm(this.maintenanceSchedulingModel);
    } catch (error) {
      console.error('Error al modificar la programación de mantenimiento.', error);
    }
  }

  updateFormState() {
    let isDisabled = false;
    let enableConsecutive = true;
    if (this.maintenanceSchedulingModel.active) {
      this.iconState = 'pi pi-lock';
      this.titleState = 'Abrir';
      this.stateMaintenanceScheduling = 'Cerrado';
      this.disabledForm = true;
      this.severityState = 'success';
      isDisabled = true;
      enableConsecutive = true;

    } else {
      this.iconState = 'pi pi-lock-open';
      this.titleState = 'Cerrar';
      this.stateMaintenanceScheduling = 'Abierto';
      this.disabledForm = false;
      this.severityState = 'danger';
      isDisabled = false;
      enableConsecutive = this.maintenanceSchedulingId !== 0;
    }

    const form = this.formMaintenanceScheduling;
    if (!this.permissionUpdatable || isDisabled) {
      form.disable();
    } else {
      form.enable();
    }
    const consecutiveControl = form.controls['consecutive'];
    const dateMaintenanceSchedulingControl = form.controls['dateMaintenanceScheduling'];
    enableConsecutive ? consecutiveControl.enable() : consecutiveControl.disable();
    enableConsecutive ? dateMaintenanceSchedulingControl.disable() : dateMaintenanceSchedulingControl.enable();
    form.controls['branchId'].enable();
  }

  validateForm(maintenanceScheduling: MaintenanceSchedulingModel) {
    this.formMaintenanceScheduling = this.formBuilder.group({
      branchId: [maintenanceScheduling.branchId, [Validators.required, Validators.min(1)]],
      consecutive: [{ value: maintenanceScheduling.consecutive, disabled: this.maintenanceSchedulingId === 0 }, [Validators.required]],
      typeMaintenanceScheduling: [maintenanceScheduling.typeMaintenanceScheduling, [Validators.required, Validators.min(1)]],
      dateMaintenanceScheduling: [maintenanceScheduling.dateMaintenanceScheduling
        ? this.dateConverterService.convertUtcToLocalDate(maintenanceScheduling.dateMaintenanceScheduling) : null, [Validators.required]],
      observation: [maintenanceScheduling.observation, [Validators.maxLength(250)]],
      active: [maintenanceScheduling.active, [Validators.required]],
    });

    this.typeMaintenanceScheduling = maintenanceScheduling.typeMaintenanceScheduling;
    this.updateFormState();
  }

  validateFormControls() {
    Object.keys(this.formMaintenanceScheduling.controls).forEach((key) => {
      const control = this.formMaintenanceScheduling.get(key);
      if (control && control.invalid) {
        console.warn(
          `${key} es inválido`,
          control.errors,
          'value:',
          control.value
        );
      }
    });
  }

  async validateTypeMaintenanceScheduling(typeMaintenanceScheduling: number) {
    try {
      const response = await firstValueFrom(this.maintenanceSchedulingService.getLastMaintenanceSchedulingByType(this.branchId, typeMaintenanceScheduling))
      this.lastDateMaintenanceScheduling = response?.dateMaintenanceScheduling ?? null;
      this.maintenanceSchedulingId = 0;
      this.lastStateMaintenanceScheduling = response?.active;
    } catch (error) {
      console.error('Error al buscar la última programación de mantenimiento.', error);
    }
  }
}
